// TaskSection.tsx
import React, { useEffect, useState } from 'react';
import TaskSlotGrid from '../../../reusable/Client/TaskSlotGrid';
import AddTaskDialog, {
  Task,
} from '../../../reusable/Client/dialog/AddTaskDialog';
import RemoveTaskDialog from '../../../reusable/Client/dialog/RemoveTaskDialog';
import { useTranslation } from 'react-i18next';

interface Props {
  /** ADM-22。確認文言に出す配送先名。以前は "ABC" 固定だった。 */
  clientName?: string;
  selectedTasks: Task[];
  setSelectedTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  isRemoveTask?: boolean;
  showErrorTooltip: boolean;
  setShowErrorTooltip: (errorTooltip: boolean) => void;
  /**
   * RV-129。保存（作成）を送っている間は作業の追加・削除・並べ替えをさせない。送った本文と
   * 画面の内容が食い違うと、結果が分からなかった作成の送り直しが別の内容になる。
   */
  disabled?: boolean;
}

const TaskAssignment: React.FC<Props> = ({
  clientName = '',
  selectedTasks,
  setSelectedTasks,
  isRemoveTask,
  showErrorTooltip,
  setShowErrorTooltip,
  disabled = false,
}) => {
  const { t } = useTranslation('client');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isRemoveTaskModalOpen, setIsRemoveTaskModalOpen] = useState(false);
  const [selectedTaskIdForRemoval, setSelectedTaskIdForRemoval] = useState<
    number | null
  >(null);
  const [draggedTaskIndex, setDraggedTaskIndex] = useState<number | null>(null);

  // RV-129。送信が始まったら、開いている追加・削除のダイアログとドラッグを閉じる
  // （送信の後に古い操作の続きが出てこないように）。
  useEffect(() => {
    if (!disabled) return;
    setIsTaskModalOpen(false);
    setIsRemoveTaskModalOpen(false);
    setDraggedTaskIndex(null);
  }, [disabled]);

  // Handle task addition
  // PR #258 の Codex の P1（FR-AC8）。ダイアログが返した作業そのものを使う。以前は ID から親の一覧
  // （別に読んだもの）で引き直し、親の一覧の取得が失敗・古いと、選んだ作業が黙って捨てられた。
  // そのため親の一覧の取得はやめた（候補はダイアログが読んで見せる）。
  const handleAddTasks = (chosenTasks: Task[]) => {
    if (disabled) return;
    setSelectedTasks((prevSelectedTasks) => {
      const chosenIds = new Set(chosenTasks.map((task) => task.id));
      const existingTaskIds = new Set(prevSelectedTasks.map((task) => task.id));

      // 既に選んでいて、今も選ばれている作業はそのまま（並びも保つ）。
      const tasksToKeep = prevSelectedTasks.filter((task) => chosenIds.has(task.id));

      // 新しく選ばれた作業を後ろに足す。
      const newTasks = chosenTasks
        .filter((task) => !existingTaskIds.has(task.id))
        .map((task) => ({ ...task, isDisabled: false }));

      return [...tasksToKeep, ...newTasks];
    });

    setIsTaskModalOpen(false);
  };

  // Handle task removal
  const handleRemoveTask = () => {
    if (selectedTaskIdForRemoval !== null && !disabled) {
      setSelectedTasks((prevSelected) =>
        prevSelected.filter((task) => task.id !== selectedTaskIdForRemoval)
      );
    }
    setIsRemoveTaskModalOpen(false);
  };

  // Handle dragging of tasks
  const onDragStart = (index: number) => {
    if (disabled) return;
    setDraggedTaskIndex(index);
  };

  const onDragEnter = (index: number) => {
    if (!disabled && draggedTaskIndex !== null && draggedTaskIndex !== index) {
      const updatedTasks = [...selectedTasks];
      const [draggedTask] = updatedTasks.splice(draggedTaskIndex, 1);
      updatedTasks.splice(index, 0, draggedTask);
      setSelectedTasks(updatedTasks);
      setDraggedTaskIndex(index);
    }
  };

  const onDragEnd = () => {
    setDraggedTaskIndex(null);
  };

  return (
    <>
      <div className="flex flex-col px-6 py-4 gap-4">
        <div className="flex flex-row justify-between mb-4">
          <div className="relative">
            <h6
              className="font-medium text-subtitle-1 relative inline-block cursor-pointer"
              onMouseEnter={() => setShowErrorTooltip(true)}
              onMouseLeave={() => setShowErrorTooltip(false)}
            >
              {t('detail.tasks')} <span className="text-error">*</span>
            </h6>
            {showErrorTooltip && (
              <div className="absolute bg-red-100 text-red-600 border border-red-300 p-2 rounded mt-1 z-10 whitespace-nowrap">
                {t('manage_client.add_task_required')}
              </div>
            )}
          </div>
        </div>
        {/* A09-3（2026-11-03 和田様の指摘）。タスク管理（A27）と同じ 3行×5列の箱に並べる。空の枠を押して追加、
            枠の右上の「×」で外す。並びは箱をドラッグして変える（左上から右下の順に保存する）。以前は開始→終了の
            矢印の上に横に並べ、「タスク削除」で切り替えるモードで外していた。 */}
        <div className="pb-4">
          <TaskSlotGrid
            tasks={selectedTasks}
            onAdd={() => setIsTaskModalOpen(true)}
            addLabel={t('manage_client.add_task')}
            onRemove={(task) => {
              if (disabled) return;
              if (isRemoveTask) {
                setSelectedTaskIdForRemoval(task.id);
                setIsRemoveTaskModalOpen(true);
              } else {
                // 登録の画面（まだ保存していない）では、記録が無いので確認せずに外す。
                setSelectedTasks((prev) => prev.filter((item) => item.id !== task.id));
              }
            }}
            removeLabel={(task) => t('manage_client.remove_task_named', { name: task.name })}
            disabled={disabled}
            slotProps={(_task, index) => ({
              className: `relative flex flex-row items-center justify-between gap-2 h-16 px-3 border rounded-md shadow-sm bg-white ${
                disabled ? '' : 'cursor-move'
              }`,
              draggable: !disabled,
              onDragStart: () => onDragStart(index),
              onDragEnter: () => onDragEnter(index),
              onDragEnd,
            })}
          />
        </div>
      </div>

      {/* Task Selection Modal */}
      {/* RV-129。送信中は作業の追加・削除のダイアログを出さない（開いていても閉じる）。 */}
      {isTaskModalOpen && !disabled && (
        <AddTaskDialog
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          tasks={selectedTasks}
          onAddTasks={handleAddTasks}
        />
      )}

      {isRemoveTaskModalOpen && !disabled && (
        <RemoveTaskDialog
          clientName={clientName}
          isOpen={isRemoveTaskModalOpen}
          onClose={() => setIsRemoveTaskModalOpen(false)}
          onDelete={handleRemoveTask}
          taskId={selectedTaskIdForRemoval}
        />
      )}
    </>
  );
};

export default TaskAssignment;
